import { Box } from '@mui/material';
import React, { Component } from 'react';
import { connect, MapStateToProps } from 'react-redux';

import { clearSelectedExercise, createExercise } from '../../actions/exercises';
import { ExerciseRecord } from '../../api/exercises.interface';
import { withRouter, WithRouterProps } from '../../common/hooks/withParams';
import { ReduxState } from '../../reducers';
import ExerciseForm from './ExerciseForm';
import { ExerciseFormErrors, ExerciseFormValues } from './exercise.types';
import { createEmptyExerciseFormValues, createExercisePayload, findDuplicateTagError, hasUnsavedExerciseChanges, validateExerciseForm } from './exercise.utils';
import UnsavedChangesDialog from './UnsavedChangesDialog';

interface ComponentProps {}

interface ReduxProps {
    savingItem: boolean;
}

interface ReduxActionProps {
    clearSelectedExercise: () => void;
    createExercise: (exercise: ExerciseRecord, navigate: (path: string) => void) => Promise<{ message?: string; success: boolean }>;
}

interface State {
    errors: ExerciseFormErrors;
    showUnsavedChangesDialog: boolean;
    values: ExerciseFormValues;
}

type Props = ComponentProps & ReduxProps & ReduxActionProps & WithRouterProps;

class CreateExercisePage extends Component<Props, State> {
    state: State = {
        errors: {},
        showUnsavedChangesDialog: false,
        values: createEmptyExerciseFormValues(),
    };

    componentDidMount() {
        this.props.clearSelectedExercise();
        window.addEventListener('beforeunload', this.onBeforeUnload);
    }

    componentWillUnmount() {
        window.removeEventListener('beforeunload', this.onBeforeUnload);
    }

    onBeforeUnload = (event: BeforeUnloadEvent) => {
        if (!this.isDirty()) {
            return undefined;
        }

        event.preventDefault();
        event.returnValue = '';
        return '';
    };

    onCancel = () => {
        if (!this.isDirty()) {
            this.props.navigate('/exercises');
            return;
        }

        this.setState({ showUnsavedChangesDialog: true });
    };

    onConfirmDiscardChanges = () => {
        this.setState({ showUnsavedChangesDialog: false });
        this.props.navigate('/exercises');
    };

    onFieldChanged = (name: keyof ExerciseFormValues, value: string) => {
        this.setState((currentState) => ({
            errors: {
                ...currentState.errors,
                [name]: undefined,
            },
            values: {
                ...currentState.values,
                [name]: value,
            },
        }));
    };

    onHideUnsavedChangesDialog = () => {
        this.setState({ showUnsavedChangesDialog: false });
    };

    onSubmit = async () => {
        const { values } = this.state;
        const validationErrors = validateExerciseForm(values);

        if (Object.keys(validationErrors).length) {
            this.setState({ errors: validationErrors });
            return;
        }

        const result = await this.props.createExercise(createExercisePayload(values), this.props.navigate);
        const duplicateTagError = findDuplicateTagError(result.message);

        if (duplicateTagError) {
            this.setState((currentState) => ({
                errors: {
                    ...currentState.errors,
                    tag: duplicateTagError,
                },
            }));
        }
    };

    isDirty() {
        return hasUnsavedExerciseChanges(createEmptyExerciseFormValues(), this.state.values);
    }

    render() {
        const { savingItem } = this.props;
        const { errors, showUnsavedChangesDialog, values } = this.state;

        return (
            <Box maxWidth={1100}>
                <ExerciseForm
                    errors={errors}
                    onCancel={this.onCancel}
                    onFieldChanged={this.onFieldChanged}
                    onSubmit={this.onSubmit}
                    savingItem={savingItem}
                    submitLabel="Create Exercise"
                    title="Create Exercise"
                    values={values}
                />
                <UnsavedChangesDialog onCancel={this.onHideUnsavedChangesDialog} onConfirm={this.onConfirmDiscardChanges} visible={showUnsavedChangesDialog} />
            </Box>
        );
    }
}

const mapStateToProps: MapStateToProps<ReduxProps, ComponentProps, ReduxState> = ({ exercises }: ReduxState) => ({
    savingItem: exercises.savingItem,
});

export default connect<ReduxProps, ReduxActionProps, ComponentProps, ReduxState>(mapStateToProps, {
    clearSelectedExercise,
    createExercise,
})(withRouter(CreateExercisePage));